import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { pathgen } from 'object-path-generator';
import { Autocomplete, Collapse, MenuItem, Popover, Spinner, TextButton, Typography, WithInfoIcon, useStateInRef, InlineSelect } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { overrideTestIds } from 'seagull/utils/TestIds';
import { useIoContext, useStore, useStoreActions } from '~/contexts';
import { useQboClasses } from '~/hooks/reactQuery/queries/useQboClasses';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { serviceModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { Accounting, getSelectedUser } from '~/types/api.model';
import { DEFAULT_LIMIT_BILLING_TIMES, DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT, DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING, DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS, DEFAULT_QBO_CLASS_ID } from './AdvancedSettingDefaults';
import { AdvancedSettingsControl } from './AdvancedSettingsControl/AdvancedSettingsControl';
import { AutoPriceIncreaseControl } from './AutoPriceIncreaseControl/AutoPriceIncreaseControl';
import { DiscountPeriodControl } from './DiscountPeriodControl/DiscountPeriodControl';
import { useGetSettings } from './hooks/useGetSettings/useGetSettings';
import { LimitedTimeDiscountOfferControl } from './LimitedTimeDiscountOfferControl/LimitedTimeDiscountOfferControl';
import { PreapprovedControl } from './PreapprovedControl/PreapprovedControl';
import styles from './ServiceAdvancedSettings.module.scss';
import { AdvancedSetting } from './ServiceAdvancedSettings.types';
import { getUserForRole, isSettingActive } from './ServiceAdvancedSettings.utils';
import { ServiceAdvancedSettingsPopover } from './ServiceAdvancedSettingsPopover/ServiceAdvancedSettingsPopover';
import { StartAutomaticBillingAtControl } from './StartAutomaticBillingAtControl/StartAutomaticBillingAtControl';
import { TextAndInlineSelectRow } from './TextAndInlineSelectRow';
export function renderAdvancedSettingControl(setting, controlProps, service) {
    switch(setting.type){
        case AdvancedSetting.Pause:
            return /*#__PURE__*/ _jsx(PausedControl, _object_spread({}, controlProps));
        case AdvancedSetting.StartAutomaticBillingAt:
            return /*#__PURE__*/ _jsx(StartAutomaticBillingAtControl, _object_spread({}, controlProps));
        case AdvancedSetting.BillOnApproval:
            return /*#__PURE__*/ _jsx(BillOnApprovalControl, _object_spread({}, controlProps));
        case AdvancedSetting.Prorate:
            return /*#__PURE__*/ _jsx(ProrateControl, _object_spread({}, controlProps));
        case AdvancedSetting.Optional:
            return /*#__PURE__*/ _jsx(OptionalControl, _object_spread({}, controlProps));
        case AdvancedSetting.LimitCharges:
            return /*#__PURE__*/ _jsx(LimitChargesControl, _object_spread({}, controlProps));
        case AdvancedSetting.AutoPriceIncrease:
            return /*#__PURE__*/ _jsx(AutoPriceIncreaseControl, _object_spread({}, controlProps));
        case AdvancedSetting.QboClasses:
            return /*#__PURE__*/ _jsx(QboClassesControl, _object_spread({}, controlProps));
        case AdvancedSetting.FinancialCents:
            {
                return /*#__PURE__*/ _jsx(FinancialCentsControl, _object_spread_props(_object_spread({}, controlProps), {
                    service: service
                }));
            }
        case AdvancedSetting.Karbon:
            {
                return /*#__PURE__*/ _jsx(KarbonControl, _object_spread_props(_object_spread({}, controlProps), {
                    service: service
                }));
            }
        case AdvancedSetting.LimitedTimeDiscountOffer:
            {
                return /*#__PURE__*/ _jsx(LimitedTimeDiscountOfferControl, _object_spread({}, controlProps));
            }
        case AdvancedSetting.LimitedTimeDiscountOfferForAgreement:
            {
                return /*#__PURE__*/ _jsx(DiscountPeriodControl, _object_spread({}, controlProps));
            }
        case AdvancedSetting.PreapprovedAmount:
            {
                return /*#__PURE__*/ _jsx(PreapprovedControl, _object_spread({}, controlProps));
            }
    }
}
const accountingName = getAccountingDetails(Accounting.QBO).fullName;
const dropdownWidth = 274;
function PausedControl(props) {
    const { service, testId: testPrefix, disabled, info, label, disabledTooltip, isReadOnly, onPausedChange } = props;
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        bi: {
            name: 'pause_billing'
        },
        isReadOnly: isReadOnly,
        info: info,
        label: label,
        checked: serviceModel.isPaused(service),
        testId: testPrefix + '-start-paused',
        disabled: disabled,
        disabledTooltip: disabledTooltip,
        onChange: (checked)=>onPausedChange && onPausedChange(service.id, checked)
    }, AdvancedSetting.Pause);
}
function BillOnApprovalControl(props) {
    const { service, testId: testPrefix, disabled, label, disabledTooltip, info, isReadOnly, onBillOnAcceptanceChange } = props;
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        bi: {
            name: 'bill_on_approval'
        },
        info: info,
        label: label,
        isReadOnly: isReadOnly,
        checked: !serviceModel.isSkipBillingOnAcceptance(service),
        testId: testPrefix + '-bill-on-acceptance',
        disabled: disabled,
        disabledTooltip: disabledTooltip,
        onChange: (checked)=>onBillOnAcceptanceChange && onBillOnAcceptanceChange(service.id, checked)
    }, AdvancedSetting.BillOnApproval);
}
function ProrateControl(props) {
    const { service, testId: testPrefix, disabled, disabledTooltip, label, info, isReadOnly, onProratedChange } = props;
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        bi: {
            name: 'prorate_billing'
        },
        info: info,
        label: label,
        isReadOnly: isReadOnly,
        checked: serviceModel.isProrated(service),
        testId: ServiceAdvancedSettingsTestIds.Prorated(testPrefix),
        disabled: disabled,
        disabledTooltip: disabledTooltip,
        onChange: (checked)=>onProratedChange && onProratedChange(service.id, checked)
    }, AdvancedSetting.Prorate);
}
function OptionalControl(props) {
    const { service, info, label, disabled, disabledTooltip, isReadOnly, onOptionalChange, testId: testPrefix } = props;
    const { logBI } = useStoreActions();
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const onChangeWithBI = (data)=>{
        logBI(10488, _object_spread_props(_object_spread({}, data), {
            serviceId: service.id,
            serviceName: service.name
        }));
        onOptionalChange && onOptionalChange(service.id, data.optional, data.includedByDefault);
    };
    const isOptional = serviceModel.isOptional(service.participation);
    const shouldUseInlineSelect = isOptional;
    const includedByDefault = isOptional && service.participation.type === 'optional' ? service.participation.optional.includedByDefault : false;
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        info: shouldUseInlineSelect ? '' : info,
        label: shouldUseInlineSelect ? '' : label,
        disabled: disabled,
        isReadOnly: isReadOnly,
        disabledTooltip: disabledTooltip,
        checked: isOptional,
        testId: testPrefix + '-toggle-optional',
        bi: {
            name: 'mark_as_optional'
        },
        onChange: (checked)=>onChangeWithBI({
                optional: checked,
                includedByDefault: DEFAULT_OPTIONAL_INCLUDED_BY_DEFAULT
            }),
        labelSuffix: shouldUseInlineSelect && /*#__PURE__*/ _jsx(WithInfoIcon, {
            info: info,
            infoTestId: "".concat(testPrefix, "-optional-info"),
            children: /*#__PURE__*/ _jsxs(TextAndInlineSelectRow, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: texts.markAsOptionalAnd()
                    }),
                    /*#__PURE__*/ _jsxs(InlineSelect, {
                        skin: "primary",
                        value: includedByDefault.toString(),
                        testId: "".concat(service.id, "-included-dropdown"),
                        bi: {
                            name: 'included_by_default'
                        },
                        disabled: isReadOnly,
                        onChange: (e)=>onChangeWithBI({
                                optional: true,
                                includedByDefault: e.target.value === 'true'
                            }),
                        SelectProps: {
                            renderValue: ()=>includedByDefault ? texts.included() : texts.notIncluded()
                        },
                        children: [
                            /*#__PURE__*/ _jsx(MenuItem, {
                                testId: "".concat(service.id, "-option-not-included"),
                                value: "false",
                                children: texts.notIncludedMenuItem()
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                testId: "".concat(service.id, "-option-included"),
                                value: "true",
                                children: texts.includedMenuItem()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        children: texts.byDefault()
                    })
                ]
            })
        })
    }, AdvancedSetting.Optional);
}
function LimitChargesControl(props) {
    const { service, showServiceValidationWarning = false, info, label, isReadOnly, onLimitBillingTimesChange, testId: testPrefix } = props;
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const { logBI } = useStoreActions();
    const [wasModified, setWasModified] = useState(false);
    const maxChargesError = serviceModel.validateMaxCharges(service).length > 0;
    const onChangeWithBI = (data)=>{
        logBI(10489, _object_spread_props(_object_spread({}, data), {
            serviceId: service.id,
            serviceName: service.name
        }));
        onLimitBillingTimesChange && onLimitBillingTimesChange(service.id, data.limit, data.count);
    };
    const isMaxChargesEnabled = serviceModel.maxChargesEnabled(service);
    const shouldUseInlineSelect = isMaxChargesEnabled;
    const maxChargesLimit = serviceModel.maxChargesLimit(service);
    const isLimitValid = maxChargesLimit != null && maxChargesLimit >= 1 && maxChargesLimit <= 99;
    const numberOptions = useMemo(()=>Array.from({
            length: 99
        }, (_, i)=>i + 1), []);
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        info: shouldUseInlineSelect ? '' : info,
        label: shouldUseInlineSelect ? '' : label,
        isReadOnly: isReadOnly,
        checked: isMaxChargesEnabled,
        testId: testPrefix + '-toggle-limit-billing-count',
        bi: {
            name: 'limit_billing_cycles_enabled'
        },
        onChange: (checked)=>{
            if (!checked) {
                onChangeWithBI({
                    limit: false,
                    count: null
                });
                return;
            }
            const count = !isLimitValid ? DEFAULT_LIMIT_BILLING_TIMES : maxChargesLimit;
            onChangeWithBI({
                limit: true,
                count
            });
        },
        labelSuffix: shouldUseInlineSelect && /*#__PURE__*/ _jsx(WithInfoIcon, {
            info: info,
            infoTestId: "".concat(testPrefix, "-limit-charges-info"),
            children: /*#__PURE__*/ _jsxs(TextAndInlineSelectRow, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: texts.bill()
                    }),
                    /*#__PURE__*/ _jsxs(InlineSelect, {
                        skin: "primary",
                        value: (maxChargesLimit === null || maxChargesLimit === void 0 ? void 0 : maxChargesLimit.toString()) || '',
                        testId: "".concat(service.id, "-limit-billing-count-input"),
                        bi: {
                            name: 'limit_billing_cycles_count'
                        },
                        disabled: isReadOnly,
                        className: styles.limitChargesSelect,
                        error: !isLimitValid || maxChargesError && (wasModified || showServiceValidationWarning),
                        onChange: (e)=>{
                            const newCount = Number(e.target.value);
                            setWasModified(true);
                            onChangeWithBI({
                                limit: true,
                                count: newCount
                            });
                        },
                        children: [
                            numberOptions.map((num)=>/*#__PURE__*/ _jsx(MenuItem, {
                                    style: {
                                        justifyContent: 'center'
                                    },
                                    value: num.toString(),
                                    testId: "".concat(service.id, "-limit-option-").concat(num),
                                    children: num
                                }, num)),
                            maxChargesError && maxChargesLimit != null && /*#__PURE__*/ _jsx(MenuItem, {
                                value: maxChargesLimit.toString(),
                                disabled: true,
                                style: {
                                    display: 'none'
                                },
                                children: maxChargesLimit
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        children: texts.times()
                    })
                ]
            })
        })
    }, AdvancedSetting.LimitCharges);
}
export var IncreaseMethod = /*#__PURE__*/ function(IncreaseMethod) {
    IncreaseMethod["EffectiveDate"] = "effectiveDate";
    IncreaseMethod["Date"] = "date";
    return IncreaseMethod;
}({});
function QboClassesControl(props) {
    const { info, label, service, testId: testPrefix, showServiceValidationWarning, isReadOnly, onQboClassChange = ()=>{} } = props;
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const { qboClasses, isLoading: isLoadingClasses } = useQboClasses();
    const allowToCustomizeQboClasses = useStore(selectors.integration.allowToCustomizeQboClasses);
    const isClassesFeatureDisabled = !allowToCustomizeQboClasses || !isLoadingClasses && qboClasses.length === 0;
    const integratedClassId = serviceModel.integratedClassId(service);
    const checked = !isClassesFeatureDisabled && integratedClassId != null;
    const errors = serviceModel.validateIntegratedClass(service);
    const selectedOption = useMemo(()=>integratedClassId && qboClasses ? qboClasses.find((qboClass)=>qboClass.id === integratedClassId) : undefined, [
        integratedClassId,
        qboClasses
    ]);
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        isReadOnly: isReadOnly,
        info: info,
        label: label,
        checked: checked,
        disabled: isClassesFeatureDisabled || isLoadingClasses,
        disabledTooltip: isClassesFeatureDisabled ? texts.qboClassDisabledTooltip({
            accountingName
        }) : undefined,
        testId: "".concat(testPrefix, "-toggle-qbo-classes"),
        bi: {
            name: 'override_qbo_class'
        },
        onChange: (newChecked)=>{
            const newIntegratedClass = newChecked ? serviceModel.makeIntegratedClassSet(DEFAULT_QBO_CLASS_ID) : serviceModel.makeIntegratedClassUnset();
            onQboClassChange(service.id, newIntegratedClass);
        },
        children: checked && /*#__PURE__*/ _jsx("div", {
            className: styles.additionalControl,
            children: /*#__PURE__*/ _jsx(Autocomplete, {
                bi: {
                    name: 'qbo_class'
                },
                selectOnFocus: true,
                multiple: false,
                disabled: isReadOnly,
                size: "small",
                value: selectedOption,
                testId: "".concat(testPrefix, "-select-qbo-class"),
                textFieldProps: {
                    label: texts.classLabel(),
                    placeholder: texts.selectClass(),
                    alwaysShrinkLabel: true,
                    width: dropdownWidth,
                    error: showServiceValidationWarning && errors.length > 0,
                    helperText: showServiceValidationWarning && errors[0]
                },
                options: qboClasses || [],
                onChange: (_, newOption)=>{
                    const id = !newOption || typeof newOption === 'string' ? DEFAULT_QBO_CLASS_ID : newOption.id;
                    onQboClassChange(service.id, serviceModel.makeIntegratedClassSet(id));
                },
                getOptionLabel: (option)=>!option ? '' : typeof option == 'string' ? option : option.name
            }, selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.id)
        })
    }, AdvancedSetting.QboClasses);
}
function FinancialCentsControl(props) {
    const { testId: testPrefix, service, onFinancialCentsChange } = props, rest = _object_without_properties(props, [
        "testId",
        "service",
        "onFinancialCentsChange"
    ]);
    const { api } = useStoreActions();
    const users = useStore(selectors.integration.financialCentsUsers);
    return /*#__PURE__*/ _jsx(PracticeManagementTeamMembersControl, _object_spread_props(_object_spread({}, rest), {
        service: service,
        getServiceIntegration: (service)=>service.integrations.financialCentsIntegration(),
        testId: "".concat(testPrefix, "-financial-cents"),
        users: users,
        updateIntegration: onFinancialCentsChange,
        getRoles: api.financialCents.getRoles
    }));
}
function KarbonControl(props) {
    const { testId: testPrefix, service, onKarbonChange } = props, rest = _object_without_properties(props, [
        "testId",
        "service",
        "onKarbonChange"
    ]);
    const { api } = useStoreActions();
    const users = useStore(selectors.integration.karbonUsers);
    return /*#__PURE__*/ _jsx(PracticeManagementTeamMembersControl, _object_spread_props(_object_spread({}, rest), {
        service: service,
        testId: "".concat(testPrefix, "-karbon"),
        users: users,
        getServiceIntegration: (service)=>service.integrations.karbonIntegration(),
        updateIntegration: onKarbonChange,
        getRoles: api.karbon.getRoles
    }));
}
function PracticeManagementTeamMembersControl(props) {
    const getProps = useStateInRef(props);
    const { type, info, label, disabled, disabledTooltip, testId: testPrefix, service, getServiceIntegration, users, isReadOnly, updateIntegration = ()=>{} } = props;
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const [roles, setRoles] = useState();
    const didFetchRolesRef = useRef(false);
    const checked = isSettingActive({
        type,
        service
    });
    useEffect(()=>{
        if (!didFetchRolesRef.current && checked) {
            const { service, getRoles } = getProps();
            const templateIds = (service.bundle ? service.bundle.services.map((s)=>s.serviceTemplateId) : [
                service.serviceTemplateId
            ]).filter((id)=>id != null);
            Promise.all(templateIds.map(getRoles)).then((results)=>{
                const allRoles = results.flat();
                const uniqueRoles = allRoles.filter((role, index, self)=>self.findIndex((r)=>r.id === role.id) === index);
                setRoles(uniqueRoles);
            });
            didFetchRolesRef.current = true;
        }
    }, [
        checked
    ]);
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        bi: {
            name: 'pm_mapping_enabled'
        },
        info: info,
        label: label,
        checked: checked,
        testId: "".concat(testPrefix, "-toggle-mapping"),
        disabled: disabled,
        isReadOnly: isReadOnly,
        disabledTooltip: disabledTooltip,
        onChange: (newChecked)=>{
            updateIntegration(service.id, {
                teamMembers: DEFAULT_PRACTICE_MANAGEMENT_TEAM_MEMBERS,
                enabled: newChecked
            }, {
                preserveExistingTeamMembers: DEFAULT_PRACTICE_MANAGEMENT_PRESERVE_EXISTING
            });
        },
        children: checked && getServiceIntegration && (roles && users ? roles.map((role)=>/*#__PURE__*/ _jsx("div", {
                className: styles.additionalControl,
                children: /*#__PURE__*/ _jsx(Autocomplete, {
                    bi: {
                        name: 'pm_user_assignment'
                    },
                    selectOnFocus: true,
                    freeSolo: true,
                    multiple: false,
                    disabled: isReadOnly,
                    size: "small",
                    value: getSelectedUser(users, role, service, getServiceIntegration),
                    testId: "".concat(testPrefix, "-select-").concat(role.id),
                    textFieldProps: {
                        label: role.name,
                        placeholder: texts.unassigned(),
                        alwaysShrinkLabel: true,
                        width: dropdownWidth
                    },
                    options: users,
                    getOptionLabel: (option)=>option.name,
                    onChange: (_, newOption)=>{
                        const newUserId = !newOption || typeof newOption === 'string' ? null : newOption.id;
                        updateIntegration(service.id, {
                            teamMembers: {
                                [role.id]: getUserForRole(newUserId)
                            }
                        }, {
                            preserveExistingTeamMembers: true
                        });
                    }
                })
            }, role.id)) : /*#__PURE__*/ _jsx(Spinner, {
            size: 40,
            centeredInBox: true,
            centerBoxHeight: 56,
            centerBoxWidth: dropdownWidth,
            testId: "".concat(testPrefix, "-mapping-loading")
        }))
    });
}
export const { ServiceAdvancedSettingsTestIds } = pathgen(undefined, overrideTestIds({
    ServiceAdvancedSettingsTestIds: {
        Root: (testPrefix)=>"".concat(testPrefix, "-advanced-settings"),
        Prorated: (testPrefix)=>"".concat(testPrefix, "-prorated")
    }
}));
export const ServiceAdvancedSettings = /*#__PURE__*/ forwardRef(function ServiceAdvancedSettings(props, ref) {
    const { service, testId, relationshipState, isOpenInitially, hideTitle, isFreshlyAdded } = props;
    const isApprovedRelationship = relationshipState.type === 'approved';
    const { t: texts } = useLocaleKeys('components.serviceAdvancedSettings');
    const featureFlags = useFeatureFlags();
    const effectiveDate = relationshipState.effectiveDate;
    const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
    const settingsMenuAnchorRef = useRef(null);
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), []);
    const settings = useGetSettings({
        isFreshlyAdded,
        isApprovedRelationship,
        service,
        now,
        effectiveDate
    });
    const activeSettingsFromService = useMemo(()=>{
        return new Set(settings.filter((setting)=>isSettingActive({
                type: setting.type,
                service
            })).map((setting)=>setting.type));
    }, [
        settings,
        service
    ]);
    const showAdvancedSettings = isOpenInitially || activeSettingsFromService.size > 0;
    useImperativeHandle(ref, ()=>({
            showAdvancedSettings
        }), [
        showAdvancedSettings
    ]);
    const renderControl = (setting)=>{
        const controlProps = _object_spread_props(_object_spread(_object_spread_props(_object_spread({
            key: setting.type
        }, props), {
            service: serviceModel.applyChanges(service)
        }), setting), {
            featureFlags
        });
        return renderAdvancedSettingControl(setting, controlProps, service);
    };
    const openSettingsMenu = useCallback(()=>setIsSettingsMenuOpen(true), []);
    const closeSettingsMenu = useCallback(()=>setIsSettingsMenuOpen(false), []);
    const handleAddSettingsClick = useCallback(()=>{
        if (props.isReadOnly) return;
        if (isSettingsMenuOpen) {
            closeSettingsMenu();
            return;
        }
        openSettingsMenu();
    }, [
        closeSettingsMenu,
        isSettingsMenuOpen,
        openSettingsMenu,
        props.isReadOnly
    ]);
    if (settings.length === 0) return null;
    if (isOpenInitially) {
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.settings,
            "data-testid": ServiceAdvancedSettingsTestIds.Root(testId),
            children: [
                !hideTitle && /*#__PURE__*/ _jsx(Typography, {
                    weight: "normal",
                    children: texts.advancedSettingsLabel()
                }),
                settings.map(renderControl)
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Collapse, {
                in: activeSettingsFromService.size > 0,
                unmountOnExit: true,
                timeout: 500,
                children: /*#__PURE__*/ _jsx("div", {
                    className: styles.settingsAnimated,
                    "data-testid": ServiceAdvancedSettingsTestIds.Root(testId),
                    children: settings.map((setting)=>{
                        const isActive = activeSettingsFromService.has(setting.type);
                        const isAvailable = !setting.disabled;
                        if (!isAvailable) return null;
                        return /*#__PURE__*/ _jsx(Collapse, {
                            in: isActive,
                            mountOnEnter: true,
                            unmountOnExit: true,
                            timeout: 350,
                            children: /*#__PURE__*/ _jsx("div", {
                                className: cn(styles.settingWrapper, !isActive && styles.exiting),
                                children: renderControl(setting)
                            })
                        }, setting.type);
                    })
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: styles.addSettingsButton,
                children: /*#__PURE__*/ _jsx(TextButton, {
                    bi: {
                        name: 'add_advanced_settings'
                    },
                    className: styles.addSettingsButton,
                    underline: "hover",
                    skin: "primary",
                    disabled: props.isReadOnly,
                    weight: "bold",
                    prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                        size: 20
                    }),
                    testId: testId + '-add-adv-settings',
                    onClick: handleAddSettingsClick,
                    ref: settingsMenuAnchorRef,
                    children: texts.addAdvancedSettings()
                })
            }),
            /*#__PURE__*/ _jsx(Popover, {
                open: isSettingsMenuOpen,
                onClose: closeSettingsMenu,
                bottomSheetHeader: {
                    title: service.name,
                    subtitle: texts.title()
                },
                anchorEl: settingsMenuAnchorRef.current,
                anchorOrigin: {
                    vertical: -8,
                    horizontal: 'left'
                },
                transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                },
                children: /*#__PURE__*/ _jsx(ServiceAdvancedSettingsPopover, _object_spread_props(_object_spread({}, props), {
                    onClose: closeSettingsMenu
                }))
            })
        ]
    });
});
